// TODO: This file is **almost** copy pasted from moxie_suite_ui/lib/apollo.tsx. In the future we can consider sharing some of the logic
import {
  ApolloLink,
  ApolloProvider,
  DefaultContext,
  from,
  NormalizedCacheObject,
  Operation,
  split,
} from "@apollo/client";
import { HttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { useAuth } from "@clerk/nextjs";
import { GetToken, SignOut } from "@clerk/types";
import merge from "deepmerge";
import { GraphQLError } from "graphql";
import { isEqual } from "lodash";
import { AppProps } from "next/app";
import { ReactNode, useMemo } from "react";
import { HASURA_CLIENT_TOKEN_KEY } from "@/config";
import { MoxieMobileApolloClient } from "@/lib/moxieMobile/MoxieMobileApolloClient";
import {
  CLERK_JWT_HASURA_TEMPLATE,
  HASURA_INVALID_JWT_ERROR_CODE,
} from "@/types";
import { DjangoMutationError } from "@/utils/djangoMutationError";
import { wsLink } from "../apollo.links";
import { getGraphqlURI } from "../apollo.utils";
import { MoxieMobileCache } from "./MoxieMobileCache";
import { fetchUsingMoxieMobile } from "./fetchUsingMoxieMobile";

const APOLLO_STATE_PROPERTY_NAME = "__APOLLO_STATE__";

const isMutation = ({ query }: Operation) => {
  const definition = getMainDefinition(query);

  return "operation" in definition && definition.operation === "mutation";
};

const getAuthorizationHeader = async (getClerkToken: GetToken) => {
  const token = await getClerkToken(CLERK_JWT_HASURA_TEMPLATE);
  if (token) {
    return {
      authorization: `Bearer ${token}`,
    };
  }
  return {};
};

let apolloClient: MoxieMobileApolloClient<NormalizedCacheObject> = null;

type ApolloContext =
  | { noAuth?: boolean; headers: { authorization: string } }
  | {
      noAuth: boolean;
      headers: { [HASURA_CLIENT_TOKEN_KEY]: string };
    };

const createApolloClient = (getClerkToken: GetToken, signOut?: SignOut) => {
  const authLink = setContext(async (_, prevContext: DefaultContext) => {
    // Get the authentication token from cookie
    const previousContext = prevContext as ApolloContext;

    const isClientTokenAuth =
      previousContext?.headers &&
      HASURA_CLIENT_TOKEN_KEY in previousContext.headers;

    const newHeaders =
      !previousContext?.noAuth && !isClientTokenAuth
        ? await getAuthorizationHeader(getClerkToken)
        : {};

    return {
      headers: {
        ...previousContext?.headers,
        ...newHeaders,
      },
    };
  });

  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ extensions }) => {
        const code = extensions?.code;
        if (code === HASURA_INVALID_JWT_ERROR_CODE) {
          signOut &&
            signOut().catch((e) => {
              console.error(`[Apollo graphQLErrors handler error]: ${e}`);
              return false;
            });
        }
        return;
      });
  });

  const handleCustomErrorsLink = new ApolloLink((operation, forward) => {
    // This is a custom (Django) mutation error handler.
    if (isMutation(operation)) {
      return forward(operation).map((response) => {
        // Hasura error: Generic error.
        if (response.errors) {
          return response;
        }

        const dataKey = Object.keys(response.data)[0];
        const data = response.data[dataKey];

        // Hasura error: Row update mutation failed - missing permission.
        if (!data) {
          response.errors = [
            new GraphQLError(
              "Unexpected mutation response - no data returned. Likely missing permission to perform this action."
            ),
            ...(response.errors || []),
          ];

          return response;
        }

        // Django error: Generic error.
        if ("ok" in data && !data.ok) {
          const { message } = data;

          if (message) {
            response.errors = [
              new DjangoMutationError(message),
              ...(response.errors || []),
            ];
          } else {
            response.errors = [
              new GraphQLError(
                "Unexpected response from Django custom mutation. No message provided"
              ),
              ...(response.errors || []),
            ];
          }
        }

        return response;
      });
    } else return forward(operation);
  });

  const httpLink = from([
    authLink,
    errorLink,
    new HttpLink({
      uri: getGraphqlURI(),
      credentials: "include",
      fetch: fetchUsingMoxieMobile,
      fetchOptions: {
        mode: "cors",
      },
    }),
  ]);

  const splitLink = () => {
    return split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink(getClerkToken),
      httpLink
    );
  };

  return new MoxieMobileApolloClient({
    ssrMode: typeof window === "undefined",
    link: from([
      handleCustomErrorsLink,
      typeof window === "undefined" ? httpLink : splitLink(),
    ]),
    cache: new MoxieMobileCache({
      typePolicies: {
        medspa: {
          fields: {
            configuration: {
              // fixes error: configuration medspa either ensure all objects
              // of type MedspasMedspaconfiguration have an ID or a custom merge function
              merge: true,
            },
          },
        },
        // Return the serial number without the moxieId suffix.
        // MoxieId is used to allow using simulated terminals by multiple users.
        StripeTerminalType: {
          fields: {
            serialNumber: {
              read(existing) {
                if (typeof existing === "string") {
                  return existing.replace(/-moxieId-.*/, "");
                }

                return existing;
              },
            },
          },
        },
      },
    }),
  });
};

function initializeApollo(
  initialState = null,
  getClerkToken: GetToken,
  signOut?: SignOut
) {
  const client = apolloClient ?? createApolloClient(getClerkToken, signOut);

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });

    // Restore the cache with the merged data
    client.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === "undefined") {
    return client;
  }

  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = client;
  }

  return client;
}

export function useApollo<Props = Record<string, unknown>>(props: Props) {
  const { getToken: getClerkToken, signOut } = useAuth();
  const state = props[APOLLO_STATE_PROPERTY_NAME];
  const store = useMemo(
    () => initializeApollo(state, getClerkToken, signOut),
    [state, getClerkToken, signOut]
  );

  return store;
}

interface MoxieMobileApolloProviderWrapperProps {
  children: ReactNode;
  pageProps: AppProps["pageProps"];
}

export const MoxieMobileApolloProviderWrapper = ({
  children,
  pageProps,
}: MoxieMobileApolloProviderWrapperProps) => {
  const apolloClient = useApollo(pageProps);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
